/**
 * This account's public key, so it can be sent to somebody.
 *
 * The recovery procedure's first instruction is "ask the person to create a new
 * account and send you their public key from Settings" — and Settings did not
 * show one. So the one step a *member* has to perform in the whole recovery
 * process was the one step that could not be performed.
 *
 * Public by construction: it is what every other member of every group already
 * seals group keys to, and what the server has stored since the account was
 * made. Nothing here is a secret, which is why it can be copied and e-mailed
 * without ceremony — and the text says so, because a long base64 string in a
 * section about encryption looks exactly like something that should not be
 * shared.
 *
 * Folded away by default. It is needed roughly once, in a bad week, and a wall
 * of base64 on the account screen would otherwise be the loudest thing on it.
 */

import { useState } from "react";
import { useT } from "../../lib/i18n.js";
import { Button } from "../../components/ui/Button.js";
import { useToast } from "../../components/ui/Toast.js";
import "./PublicKeySection.css";

export function PublicKeySection({ publicKey }: { publicKey: string | null }) {
  const t = useT();
  const { toast } = useToast();
  const [shown, setShown] = useState(false);

  /*
   * Hidden entirely rather than shown empty. An account made before keys
   * existed has none, and an empty box under a heading invites somebody to
   * wonder what they have lost.
   */
  if (!publicKey) return null;

  return (
    <section className="fc-account__section">
      <h2>{t("Your public key")}</h2>

      <p className="fc-account__hint">
        {t(
          "Not a secret. It is how others seal photos so only you can open them, and it is what an administrator needs if you ever have to be let back into a group.",
        )}
      </p>

      {shown ? (
        <>
          <code className="fc-pubkey">{publicKey}</code>

          <div className="fc-pubkey__actions">
            <Button
              variant="secondary"
              onClick={() => {
                void navigator.clipboard
                  .writeText(publicKey)
                  .then(() => toast(t("Copied.")))
                  .catch(() =>
                    toast(
                      t("Couldn't copy — select the text and copy it manually."),
                      { variant: "danger" },
                    ),
                  );
              }}
            >
              {t("Copy key")}
            </Button>

            {/*
              A file, because this is usually pasted into an e-mail and a long
              base64 line is exactly the thing a mail client wraps and breaks.
            */}
            <Button variant="ghost" onClick={() => save(publicKey)}>
              {t("Download")}
            </Button>

            <Button variant="ghost" onClick={() => setShown(false)}>
              {t("Hide")}
            </Button>
          </div>
        </>
      ) : (
        <Button variant="secondary" onClick={() => setShown(true)}>
          {t("Show my public key")}
        </Button>
      )}
    </section>
  );
}

function save(publicKey: string): void {
  const blob = new Blob([`${publicKey}\n`], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "my-public-key.txt";
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
